import { useEffect, useId, useRef, useState } from "react";
import { flushSync } from "react-dom";
import PropTypes from "prop-types";
import { ExternalLink, SquarePen, StickyNote, Trash2 } from "lucide-react";
import { toast } from "sonner";

import { AttachmentGroup } from "@/components/ui/attachment";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import ACL_RELATIONSHIPS from "acl-relationships";
import { formatDate } from "components/helpers";
import ConfirmDeleteDialog from "components/modals/ConfirmDeleteDialog";
import NoteForm from "components/forms/NoteForm";
import { useNoteEditing } from "components/NoteEditingContext";
import NoteSourceBadge from "components/NoteSourceBadge";
import { cn } from "@/lib/utils";
import NoteContent, { wasNoteEdited } from "components/NoteContent";
import NoteVersions from "components/NoteVersions";
import SavedAttachment from "components/SavedAttachment";
import useAuthorized from "hooks/useAuthorized";
import ActivityService from "services/Activity";
import UserService from "services/User";
import useAuthStore from "stores/useAuthStore";

async function deleteNote(note, { slug, modelId } = {}) {
  const opts = slug ? { slug, modelId } : {};
  await ActivityService.deleteActivityLog(note.model_type, note.id, opts);
}

const modelMap = {
  AuditLog: "Activity Log",
};

const NoteItem = props => {
  const {
    note,
    categories,
    handleTabChange,
    onChange,
    slug,
    modelId,
    // The activity log renders the same card from an audit entry, which keeps
    // the note's files and its earlier bodies beside the note rather than on
    // it: the entry is immutable, so it lists files removed since.
    attachments,
    versions,
    // The activity log scrolls a deep-linked entry into view and highlights
    // it, and this card is what a note entry renders.
    ref,
    className,
  } = props;

  const [deleteOpen, setDeleteOpen] = useState(false);
  const editButtonRef = useRef();

  const cardKey = useId();
  const { editingKey, requestEdit, endEdit, releaseEdit, setDirty } =
    useNoteEditing();
  const isEditing = editingKey === cardKey;

  useEffect(() => () => releaseEdit(cardKey), [cardKey, releaseEdit]);

  const canEdit = useAuthorized([ACL_RELATIONSHIPS.activity.edit]);
  const canDelete = useAuthorized([ACL_RELATIONSHIPS.activity.delete]);

  const userId = useAuthStore(state => state.userAuth?.id);

  const isNoteOwnerOrAdmin =
    note.creator_user_id === userId || UserService.isAdmin();

  const handleDelete = async () => {
    try {
      await deleteNote(note, { slug, modelId });
      await onChange();
      toast.success("Note deleted");
      setDeleteOpen(false);
    } catch {
      toast.error("Failed to delete note");
    }
  };

  const wasEdited = wasNoteEdited(note);

  const category =
    note.category_id && categories.find(({ key }) => key === note.category_id);

  const showOriginalLink = note.info && note.info.refers_to_id;
  const files = attachments ?? note.attachments ?? [];
  const liveFiles = files.filter(file => !file.deleted_at);

  // The Edit button is hidden while the form is open, so it has to render
  // again before it can take focus back.
  const finishEdit = () => {
    flushSync(endEdit);
    editButtonRef.current?.focus();
  };

  const handleEditSuccess = () => {
    finishEdit();
    return onChange();
  };

  return (
    <Card size="sm" ref={ref} className={cn("tw:w-full", className)}>
      <CardContent>
        <div className="tw:flex tw:gap-3">
          <StickyNote
            className="tw:mt-0.5 tw:size-6 tw:shrink-0 tw:text-[var(--color-text-secondary)]"
            aria-hidden="true"
          />
          <div className="tw:min-w-0 tw:flex-1">
            <div className="tw:flex tw:items-start tw:gap-2">
              <div className="tw:min-w-0 tw:flex-1">
                <h4 className="tw:m-0! tw:leading-5 tw:[font-family:var(--font-heading)] tw:text-sm tw:font-semibold tw:text-[var(--color-text-primary)]">
                  Note
                </h4>
                <div className="tw:flex tw:flex-wrap tw:items-baseline tw:gap-x-2 tw:text-sm tw:text-[var(--color-text-secondary)]">
                  <span>
                    Submitted by{" "}
                    <strong className="tw:text-[var(--color-text-primary)]">
                      {note.creator_user_full_name}
                    </strong>
                  </span>
                  <span>{formatDate(note.created_at)}</span>
                  {wasEdited && (
                    <span className="tw:text-xs tw:text-[var(--color-text-muted)]">
                      (edited {formatDate(note.updated_at)})
                    </span>
                  )}
                  {category && (
                    <Badge variant="secondary" size="sm">
                      {category.text}
                    </Badge>
                  )}
                  <NoteSourceBadge
                    sourceName={note.source_name}
                    modelType={note.model_type}
                    modelId={note.model_id}
                  />
                </div>
              </div>
              <div className="tw:flex tw:shrink-0 tw:gap-1">
                {canEdit && isNoteOwnerOrAdmin && !isEditing && (
                  <Tooltip>
                    <TooltipTrigger
                      render={
                        <Button
                          ref={editButtonRef}
                          variant="ghost"
                          size="icon-sm"
                          aria-label="Edit note"
                          onClick={() => requestEdit(cardKey)}
                        >
                          <SquarePen />
                        </Button>
                      }
                    />
                    <TooltipContent>Edit note</TooltipContent>
                  </Tooltip>
                )}
                {canDelete && !isEditing && (
                  <Tooltip>
                    <TooltipTrigger
                      render={
                        <Button
                          variant="ghost"
                          size="icon-sm"
                          aria-label="Delete note"
                          onClick={() => setDeleteOpen(true)}
                        >
                          <Trash2 />
                        </Button>
                      }
                    />
                    <TooltipContent>Delete note</TooltipContent>
                  </Tooltip>
                )}
              </div>
            </div>

            {isEditing ? (
              <div className="tw:mt-3">
                <NoteForm
                  note={{ ...note, attachments: liveFiles }}
                  modelId={note.model_id}
                  modelType={note.model_type}
                  slug={slug}
                  categories={categories}
                  updateCategories={onChange}
                  onSuccess={handleEditSuccess}
                  onCancel={finishEdit}
                  onDirtyChange={setDirty}
                />
              </div>
            ) : (
              <NoteContent
                note={note}
                className="tw:mt-3! tw:max-h-96 tw:overflow-y-auto"
              />
            )}

            {!isEditing && files.length > 0 && (
              <AttachmentGroup className="tw:mt-2 tw:flex-wrap">
                {files.map(attachment => (
                  <SavedAttachment
                    key={attachment.id ?? attachment.url}
                    attachment={attachment}
                    size="xs"
                  />
                ))}
              </AttachmentGroup>
            )}

            <NoteVersions versions={versions} />

            {showOriginalLink && (
              <Button
                variant="link"
                size="sm"
                className="tw:mt-2 tw:h-auto tw:px-0"
                onClick={e =>
                  handleTabChange &&
                  handleTabChange(e, {
                    name: "activityLog",
                    auditLogId: note.info.refers_to_id,
                  })
                }
              >
                See Original {modelMap[note.info.refers_to_model]}
                <ExternalLink data-icon="inline-end" />
              </Button>
            )}
          </div>
        </div>
      </CardContent>
      <ConfirmDeleteDialog
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        title="Delete note?"
        description="Are you sure you want to delete this note?"
        onConfirm={handleDelete}
      />
    </Card>
  );
};

NoteItem.propTypes = {
  note: PropTypes.shape({
    id: PropTypes.number.isRequired,
    model_id: PropTypes.number.isRequired,
    model_type: PropTypes.string.isRequired,
    creator_user_id: PropTypes.number,
    creator_user_full_name: PropTypes.string,
    category_id: PropTypes.number,
    created_at: PropTypes.string,
    updated_at: PropTypes.string,
    source_name: PropTypes.string,
    info: PropTypes.object,
    attachments: PropTypes.arrayOf(PropTypes.object),
  }).isRequired,
  categories: PropTypes.arrayOf(PropTypes.object).isRequired,
  handleTabChange: PropTypes.func,
  onChange: PropTypes.func.isRequired,
  slug: PropTypes.string,
  modelId: PropTypes.number,
  attachments: PropTypes.arrayOf(PropTypes.object),
  versions: PropTypes.arrayOf(PropTypes.object),
};

export default NoteItem;
